/**
 * v1.14.58 — Admin AI Assistant
 *
 * Layout 2-col:
 *   - sx: list conversazioni + nuova
 *   - dx: chat (messaggi + input)
 * Tool calls visibili come accordion espandibili.
 * Suggested prompts welcome box quando conv vuota.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Plus, Trash2, Send, Wrench, RefreshCw, Loader2, BarChart3 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axc = { withCredentials: true };

const SUGGESTED_PROMPTS = [
  "Quanti lead SOTIMI in totale? Dammi anche un breakdown per professione.",
  "Trovami tutti i lead iscritti a Pizza Park ultimi 7 giorni.",
  "Quante richieste RC Auto abbiamo ricevuto questa settimana?",
  "Riassumimi le ultime 5 richieste assicurative aperte.",
  "Bozza email follow-up per un lead Ortopedista SOTIMI con premio attuale €4.500.",
];

function MarkdownLite({ text }) {
  // Render markdown-lite molto semplice (no dipendenze esterne)
  const lines = (text || "").split("\n");
  const out = [];
  let inCodeBlock = false;
  let inTable = false;
  let codeBuf = [];
  lines.forEach((line, idx) => {
    if (line.startsWith("```")) {
      if (inCodeBlock) {
        out.push(<pre key={`c${idx}`} className="bg-zinc-900 text-emerald-200 text-xs p-3 rounded overflow-x-auto my-2">{codeBuf.join("\n")}</pre>);
        codeBuf = []; inCodeBlock = false;
      } else inCodeBlock = true;
      return;
    }
    if (inCodeBlock) { codeBuf.push(line); return; }
    // headings
    if (line.startsWith("### ")) {
      out.push(<h3 key={idx} className="text-base font-bold mt-3 mb-1">{line.slice(4)}</h3>); return;
    }
    if (line.startsWith("## ")) {
      out.push(<h2 key={idx} className="text-lg font-bold mt-3 mb-1">{line.slice(3)}</h2>); return;
    }
    if (line.startsWith("# ")) {
      out.push(<h1 key={idx} className="text-xl font-bold mt-3 mb-1">{line.slice(2)}</h1>); return;
    }
    // table row
    if (line.startsWith("|") && line.endsWith("|")) {
      if (!inTable) inTable = true;
      const cells = line.split("|").slice(1, -1).map(c => c.trim());
      const isSeparator = cells.every(c => /^[-: ]+$/.test(c));
      if (isSeparator) return;
      out.push(
        <div key={idx} className="grid grid-flow-col auto-cols-fr gap-2 text-xs border-b border-zinc-200 py-1">
          {cells.map((c, i) => <div key={i} className="px-1">{renderInline(c)}</div>)}
        </div>
      );
      return;
    }
    inTable = false;
    // bullet list
    if (/^\s*[-*]\s+/.test(line)) {
      out.push(<li key={idx} className="ml-5 list-disc text-sm">{renderInline(line.replace(/^\s*[-*]\s+/, ""))}</li>);
      return;
    }
    if (line.trim() === "---") {
      out.push(<hr key={idx} className="my-2 border-zinc-200" />); return;
    }
    if (!line.trim()) { out.push(<div key={idx} className="h-2" />); return; }
    out.push(<p key={idx} className="text-sm leading-relaxed">{renderInline(line)}</p>);
  });
  return <div className="space-y-1">{out}</div>;
}

function renderInline(text) {
  // Render bold **xxx** e italic *xxx* via regex split
  const parts = [];
  let remaining = text;
  let key = 0;
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  let m;
  let lastIdx = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(<span key={key++}>{text.slice(lastIdx, m.index)}</span>);
    const tok = m[0];
    if (tok.startsWith("**")) parts.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("`")) parts.push(<code key={key++} className="bg-zinc-100 px-1 rounded text-xs">{tok.slice(1, -1)}</code>);
    else parts.push(<em key={key++}>{tok.slice(1, -1)}</em>);
    lastIdx = m.index + tok.length;
  }
  if (lastIdx < text.length) parts.push(<span key={key++}>{text.slice(lastIdx)}</span>);
  return parts;
}

function ToolCallBadge({ tc, idx }) {
  const [open, setOpen] = useState(false);
  let result;
  try { result = JSON.parse(tc.result_preview); } catch { result = tc.result_preview; }
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 my-2 text-xs" data-testid={`tool-call-${idx}`}>
      <button onClick={() => setOpen(o => !o)} className="flex items-center gap-2 w-full text-left font-mono text-emerald-800 font-semibold">
        <Wrench className="w-3.5 h-3.5" /> {tc.tool}({Object.keys(tc.args || {}).join(", ") || "—"})
        <span className="ml-auto text-emerald-600">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="mt-2 bg-white rounded p-2 max-h-72 overflow-y-auto" data-testid={`tool-call-result-${idx}`}>
          <div className="text-zinc-500 text-[10px] uppercase mb-1">args</div>
          <pre className="text-[11px] bg-zinc-50 p-2 rounded mb-2 overflow-x-auto">{JSON.stringify(tc.args, null, 2)}</pre>
          <div className="text-zinc-500 text-[10px] uppercase mb-1">result preview</div>
          <pre className="text-[11px] bg-zinc-50 p-2 rounded overflow-x-auto whitespace-pre-wrap">{typeof result === "object" ? JSON.stringify(result, null, 2) : String(result)}</pre>
        </div>
      )}
    </div>
  );
}

export default function AdminAIAssistantPage() {
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [routerStats, setRouterStats] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const chatRef = useRef(null);

  const loadConversations = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/ai-assistant/conversations`, axc);
      setConversations(data.items || []);
    } catch (e) {
      if (e.response?.status === 401) toast.error("Sessione scaduta.");
    }
  };

  const loadConv = async (id) => {
    if (!id) { setMessages([]); setActiveConvId(null); return; }
    try {
      const { data } = await axios.get(`${API}/admin/ai-assistant/conversations/${id}`, axc);
      setMessages(data.messages || []);
      setActiveConvId(id);
    } catch (e) {
      toast.error("Conversazione non caricata");
    }
  };

  const sendMessage = async (msg) => {
    const text = (msg ?? input).trim();
    if (!text) return;
    setLoading(true);
    setInput("");
    // ottimistic
    setMessages(m => [...m, { role: "user", content: text, timestamp: new Date().toISOString() }]);
    try {
      const { data } = await axios.post(`${API}/admin/ai-assistant/chat`, {
        conversation_id: activeConvId,
        message: text,
      }, { ...axc, timeout: 120000 });
      setActiveConvId(data.conversation_id);
      // ricarica i messaggi pieni dal server
      const { data: full } = await axios.get(`${API}/admin/ai-assistant/conversations/${data.conversation_id}`, axc);
      setMessages(full.messages || []);
      loadConversations();
    } catch (e) {
      toast.error(`Errore: ${e.response?.data?.detail || e.message}`);
      setMessages(m => [...m, { role: "assistant", content: "❌ Errore di rete. Riprova.", timestamp: new Date().toISOString() }]);
    } finally {
      setLoading(false);
    }
  };

  const archiveConv = async (id) => {
    if (!window.confirm("Archiviare questa conversazione?")) return;
    try {
      await axios.delete(`${API}/admin/ai-assistant/conversations/${id}`, axc);
      if (activeConvId === id) { setMessages([]); setActiveConvId(null); }
      loadConversations();
    } catch (e) { toast.error("Errore archiviazione"); }
  };

  const loadStats = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/ai-assistant/router-stats?days=7`, axc);
      setRouterStats(data);
      setShowStats(true);
    } catch (e) { toast.error("Stats non disponibili"); }
  };

  useEffect(() => { loadConversations(); }, []);
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 h-[calc(100vh-160px)] min-h-[600px]" data-testid="ai-assistant-page">
      {/* SIDEBAR */}
      <Card className="overflow-hidden flex flex-col" data-testid="conv-sidebar">
        <CardHeader className="pb-2 border-b">
          <CardTitle className="text-sm flex items-center justify-between">
            <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-indigo-600" /> Conversazioni</span>
            <Button size="sm" variant="ghost" onClick={() => { setMessages([]); setActiveConvId(null); }} data-testid="new-conv-btn">
              <Plus className="w-3 h-3" /> Nuova
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto p-2 space-y-1">
          <button onClick={loadStats} className="w-full text-left text-xs px-3 py-2 rounded bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 flex items-center gap-2" data-testid="router-stats-btn">
            <BarChart3 className="w-3.5 h-3.5" /> Router stats (7gg)
          </button>
          {conversations.length === 0 && (
            <div className="text-xs text-zinc-400 p-3 italic">Nessuna conversazione</div>
          )}
          {conversations.map(c => (
            <div key={c.id} className={`group flex items-center gap-1 rounded px-2 py-2 cursor-pointer ${activeConvId === c.id ? "bg-indigo-100" : "hover:bg-zinc-100"}`} data-testid={`conv-item-${c.id}`}>
              <div className="flex-1 min-w-0" onClick={() => loadConv(c.id)}>
                <div className="text-xs font-semibold truncate">{c.title || "Senza titolo"}</div>
                <div className="text-[10px] text-zinc-500">{c.updated_at?.slice(0, 16).replace("T", " ")}</div>
                {c.last_model_used && <div className="text-[10px] text-indigo-700 truncate">{c.last_model_used}</div>}
              </div>
              <button onClick={() => archiveConv(c.id)} className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-500 p-1" data-testid={`archive-${c.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* CHAT */}
      <Card className="overflow-hidden flex flex-col" data-testid="chat-panel">
        <CardHeader className="pb-2 border-b bg-gradient-to-r from-indigo-50 to-emerald-50">
          <CardTitle className="text-sm flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-600" /> AI Assistant Colucci
              <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-bold">PREMIUM</span>
            </span>
            {activeConvId && <span className="text-[10px] text-zinc-500 font-mono">{activeConvId.slice(0, 8)}</span>}
          </CardTitle>
        </CardHeader>
        <CardContent ref={chatRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-zinc-50" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="text-center py-8" data-testid="welcome-box">
              <Sparkles className="w-12 h-12 mx-auto text-indigo-400 mb-2" />
              <h2 className="text-lg font-bold text-zinc-800 mb-1">Come posso aiutarti oggi?</h2>
              <p className="text-xs text-zinc-500 mb-6">Cerca lead, analizza eventi, prepara bozze email — basta chiedere.</p>
              <div className="grid sm:grid-cols-2 gap-2 max-w-2xl mx-auto">
                {SUGGESTED_PROMPTS.map((p, i) => (
                  <button key={i} onClick={() => sendMessage(p)} className="text-left text-xs bg-white border border-zinc-200 hover:border-indigo-400 rounded-lg p-3 transition" data-testid={`suggested-${i}`}>
                    💡 {p}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.filter(m => m.role === "user" || m.role === "assistant").map((m, idx) => (
            <div key={idx} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${idx}-${m.role}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.role === "user" ? "bg-indigo-600 text-white" : "bg-white border border-zinc-200"}`}>
                {m.role === "user" ? (
                  <div className="text-sm whitespace-pre-wrap">{m.content}</div>
                ) : (
                  <>
                    {(m.tool_calls || []).map((tc, i) => <ToolCallBadge key={i} idx={i} tc={tc} />)}
                    <MarkdownLite text={m.content} />
                    {m.model_used && <div className="text-[10px] text-zinc-400 mt-2 font-mono">⚙ {m.model_used}</div>}
                  </>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start" data-testid="loading-spinner">
              <div className="bg-white border border-zinc-200 rounded-2xl px-4 py-3 inline-flex items-center gap-2 text-sm text-zinc-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Sto pensando…
              </div>
            </div>
          )}
        </CardContent>
        <div className="border-t p-3 bg-white">
          <div className="flex gap-2 items-end">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="Chiedi qualsiasi cosa sul tuo CRM (Enter per inviare, Shift+Enter per nuova riga)…"
              className="min-h-[60px] max-h-[200px] flex-1 text-sm"
              disabled={loading}
              data-testid="chat-input"
            />
            <Button onClick={() => sendMessage()} disabled={loading || !input.trim()} className="h-[60px] px-4" data-testid="send-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </Card>

      {/* Router Stats Modal */}
      {showStats && routerStats && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowStats(false)} data-testid="router-stats-modal">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white">
              <h2 className="font-bold text-lg flex items-center gap-2"><BarChart3 className="w-5 h-5" /> AI Router stats — ultimi {routerStats.period_days} giorni</h2>
              <Button size="sm" variant="ghost" onClick={() => setShowStats(false)}>✕</Button>
            </div>
            <div className="p-4 space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                  <div className="text-[10px] uppercase text-indigo-600 font-bold">Chiamate totali</div>
                  <div className="text-2xl font-bold">{routerStats.total_calls}</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                  <div className="text-[10px] uppercase text-emerald-600 font-bold">Costo stimato</div>
                  <div className="text-2xl font-bold">${routerStats.total_cost_est_usd}</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <div className="text-[10px] uppercase text-amber-700 font-bold">Premium downgrade</div>
                  <div className="text-2xl font-bold">{(routerStats.premium_downgrade_rate * 100).toFixed(1)}%</div>
                </div>
              </div>
              <div>
                <h3 className="font-bold mb-1">Top call_site</h3>
                <div className="space-y-1">
                  {routerStats.top_call_sites.map((s, i) => (
                    <div key={i} className="flex justify-between border-b border-zinc-100 py-1 text-xs">
                      <code className="text-indigo-700">{s._id}</code>
                      <span className="text-zinc-600">{s.count} · {s.avg_latency_ms}ms avg</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-bold mb-1">By model</h3>
                <div className="space-y-1">
                  {routerStats.by_model.map((m, i) => (
                    <div key={i} className="flex justify-between border-b border-zinc-100 py-1 text-xs">
                      <code>{m._id || "?"}</code>
                      <span className="text-zinc-600">{m.count} · downgrade {m.downgrade_count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
